import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, Eye, EyeOff, ChefHat } from 'lucide-react';
import { toast } from 'sonner';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  
  const { login, loginWithGoogle, resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (resetMode) {
        await resetPassword(email);
        toast.success('Passwort-Reset E-Mail wurde gesendet!');
        setResetMode(false);
      } else {
        await login(email, password);
        toast.success('Erfolgreich angemeldet!');
        navigate('/');
      }
    } catch (error) {
      console.error('Auth error:', error);
      if (error.code === 'auth/user-not-found') {
        toast.error('Benutzer nicht gefunden');
      } else if (error.code === 'auth/wrong-password') {
        toast.error('Falsches Passwort');
      } else if (error.code === 'auth/invalid-email') {
        toast.error('Ungültige E-Mail-Adresse');
      } else {
        toast.error('Anmeldung fehlgeschlagen');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle();
      toast.success('Erfolgreich mit Google angemeldet!');
      navigate('/');
    } catch (error) {
      console.error('Google login error:', error);
      toast.error('Google-Anmeldung fehlgeschlagen');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <ChefHat className="w-10 h-10 text-terracotta" />
              <span className="font-display text-2xl font-bold text-navy">Schmackofatz</span>
            </Link>
            <h1 className="font-display text-2xl font-bold text-navy">
              {resetMode ? 'Passwort zurücksetzen' : 'Willkommen zurück'}
            </h1>
            <p className="text-olive mt-2">
              {resetMode 
                ? 'Gib deine E-Mail-Adresse ein' 
                : 'Melde dich an, um fortzufahren'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-navy">E-Mail</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-olive" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="deine@email.de"
                  className="pl-10 py-5 rounded-xl"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            {!resetMode && (
              <div>
                <Label htmlFor="password" className="text-navy">Passwort</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-olive" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 pr-10 py-5 rounded-xl"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-olive hover:text-navy"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-terracotta hover:bg-terracotta/90 text-white rounded-xl py-5 font-semibold"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Bitte warten...' : (resetMode ? 'E-Mail senden' : 'Anmelden')}
            </Button>
          </form>

          {/* Forgot Password */}
          <button
            type="button"
            onClick={() => setResetMode(!resetMode)}
            className="w-full text-center mt-4 text-sm text-terracotta hover:underline"
            data-testid="forgot-password-btn"
          >
            {resetMode ? 'Zurück zur Anmeldung' : 'Passwort vergessen?'}
          </button>

          {!resetMode && (
            <>
              {/* Divider */}
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-olive text-sm">oder</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {/* Google Login */}
              <Button
                type="button"
                variant="outline"
                onClick={handleGoogleLogin}
                className="w-full rounded-xl py-5 font-semibold gap-2"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Mit Google anmelden
              </Button>

              {/* Register Link */}
              <p className="text-center mt-6 text-olive">
                Noch kein Konto?{' '}
                <Link to="/registrieren" className="text-terracotta font-semibold hover:underline">
                  Jetzt registrieren
                </Link>
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};
